import { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Center, Flex, HStack } from '@chakra-ui/react';

import { QuotesFilters, QuoteStatus } from '__generated__/GQL';
import {
  isFetchingMore,
  isLoading,
  isSettingVariables,
} from 'common/graphql/networkStatus';
import useDebounce from 'common/hooks/useDebounce';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineLoader from 'components/_core/SunshineLoader';
import { logEvent } from 'features/Analytics/analytics';
import * as companyRoutes from 'features/Company/routes';
import * as Layout from 'layouts/ContentLayout';

import Filters from '../../../../components/Filters';
import { QuoteListBody } from '../../../../components/WorkListBody';
import * as routes from '../../../../routes';
import { useFilters } from '../../../hooks/useFilters';
import { useGetQuotes } from '../../../hooks/useGetQuotes';
import { QuoteModalsProvider } from '../../hooks/useQuoteModals';
import { QUOTES_FILTERS_STATUSES, STATUSES_OPTIONS } from './constants';
import locales from './locales';
import { QuoteFilterStatus } from './types';

const ListPage: FC = () => {
  const { filters, updateFilters } = useFilters();

  const searchInput = useDebounce(filters.q);
  const statusFilters = useDebounce(filters.statuses);

  const queryFilters = useMemo<QuotesFilters>(
    () => ({
      searchInput: searchInput || null,
      status: statusFilters?.reduce<QuoteStatus[] | null>(
        (acc, selectedFilter) => {
          const selectedFilters =
            QUOTES_FILTERS_STATUSES[selectedFilter as QuoteFilterStatus];
          return acc ? [...acc, ...selectedFilters] : selectedFilters;
        },
        null,
      ),
    }),
    [statusFilters, searchInput],
  );

  const { fetchMore, networkStatus, pageInfo, quotes } =
    useGetQuotes(queryFilters);

  const loading = isLoading(networkStatus) || isSettingVariables(networkStatus);

  const onFetchMore = () => {
    logEvent({
      name: 'Quotes More Loaded',
      properties: {
        from: 'QuoteList',
        status: statusFilters?.length ? statusFilters : 'all',
      },
      usePrefix: false,
    });
    fetchMore();
  };

  return (
    <QuoteModalsProvider>
      <Layout.Root>
        <Layout.Body size="md">
          <Flex justifyContent="space-between" marginBottom="space-32">
            <Heading size="lg">{locales.title}</Heading>

            <HStack spacing="space-16">
              <Button
                as={Link}
                icon="settings"
                iconPosition="left"
                to={companyRoutes.getCompanyTabPath('invoicing')}
                variant="secondary"
              >
                {locales.presets}
              </Button>

              <Button
                as={Link}
                icon="plus"
                iconPosition="left"
                onClick={() =>
                  logEvent({
                    name: 'Add Quote',
                    properties: { from: 'QuoteListPage' },
                  })
                }
                to={routes.addQuote}
                variant="primary"
              >
                {locales.add}
              </Button>
            </HStack>
          </Flex>

          <Filters
            onStatusChange={(values) => {
              logEvent({
                name: 'Quotes Status Selected',
                properties: { status: values.length ? values : 'all' },
                usePrefix: false,
              });
              updateFilters({ ...filters, statuses: values });
            }}
            placeholder={locales.searchbar}
            statuses={STATUSES_OPTIONS}
          />

          {loading ? (
            <Center>
              <SunshineLoader />
            </Center>
          ) : (
            <QuoteListBody
              hasNextPage={pageInfo?.hasNextPage ?? false}
              isFetchingMore={isFetchingMore(networkStatus)}
              onFetchMore={onFetchMore}
              quotes={quotes}
            />
          )}
        </Layout.Body>
      </Layout.Root>
    </QuoteModalsProvider>
  );
};

export default ListPage;
